// src/pages/Discovery.js
import React from "react";
import "./Discovery.css";

// Adjust these paths to wherever you store the images
import g1 from "./g1.png";
import g2 from "./g2.png";
import g3 from "./g3.png";
import g4 from "./g4.png";
import g5 from "./g5.png";
import g6 from "./g6.png";
import g7 from "./g7.png";
import g8 from "./g8.png";
import g9 from "./g9.png";
import g10 from "./g10.png";
import g11 from "./g11.png";
import g12 from "./g12.png";
import g13 from "./g13.png";
import g14 from "./g14.png";

const Discovery = () => {
  return (
    <main className="discovery">
      {/* Intro Section */}
      <section className="discovery-intro">
        <h1 className="discovery-title">Discovery & Exploratory Analysis</h1>
        <p className="discovery-subtitle">
          This page summarizes the key exploratory graphs we used to understand
          how shot quality, context, and player attributes influence the
          probability of scoring. Each visualization helped us build intuition
          before fitting models and guided which features we included in our
          final analysis.
        </p>
      </section>

      {/* Graph Cards */}
      <section className="discovery-graphs">
        {/* 1. Shot Distance Distribution */}
        <article className="graph-card">
          <h2 className="graph-title">Shot Distance Distribution</h2>
          <img src={g1} alt="Shot distance distribution" className="graph-image" />
          <p className="graph-text">
            We plotted the distribution of shot distances to see where most shots
            are taken from and to check for outliers. This helped shape how we
            treat distance in our model.
          </p>
        </article>

        {/* 2. Raw Shot Angle – Goals vs Misses */}
        <article className="graph-card">
          <h2 className="graph-title">
            Raw Shot Angle Distribution (Goals vs Misses)
          </h2>
          <img src={g2} alt="Raw shot angle distribution" className="graph-image" />
          <p className="graph-text">
            Shows how goal vs miss angles differ before adjusting for rink side
            or handedness effects.
          </p>
        </article>

        {/* 3. Angle-Adjusted Distribution */}
        <article className="graph-card">
          <h2 className="graph-title">Angle-Adjusted Distribution</h2>
          <img src={g3} alt="Angle-adjusted distribution" className="graph-image" />
          <p className="graph-text">
            After normalizing angle, we re-examined goal vs miss distributions to
            check whether the angle effect persists independent of shooting side.
          </p>
        </article>

        {/* 4. Goals vs Misses (comparison) */}
        <article className="graph-card">
          <h2 className="graph-title">Goals vs Misses Comparison</h2>
          <img src={g4} alt="Goals vs misses comparison" className="graph-image" />
          <p className="graph-text">
            Direct comparison showing differences in distributions between goals
            and misses across key shot variables.
          </p>
        </article>

        {/* 5. Shot Distance by Outcome */}
        <article className="graph-card">
          <h2 className="graph-title">Shot Distance by Outcome</h2>
          <img src={g5} alt="Shot distance by outcome" className="graph-image" />
          <p className="graph-text">
            Highlights how much closer scoring shots typically occur compared to
            missed or saved shots.
          </p>
        </article>

        {/* 6. Shot Type Frequency */}
        <article className="graph-card">
          <h2 className="graph-title">Shot Type Frequency</h2>
          <img src={g6} alt="Shot type frequency" className="graph-image" />
          <p className="graph-text">
            Displays how common each shot type is. This context helps interpret
            goal rates later.
          </p>
        </article>

        {/* 7. Goal Rate by Shot Type */}
        <article className="graph-card">
          <h2 className="graph-title">Goal Rate by Shot Type</h2>
          <img src={g7} alt="Goal rate by shot type" className="graph-image" />
          <p className="graph-text">
            Compares shot-type efficiency by examining which shot types score at
            the highest percentage.
          </p>
        </article>

        {/* 8. Goal Rate vs Distance (binned) */}
        <article className="graph-card">
          <h2 className="graph-title">Goal Rate vs Distance (Binned)</h2>
          <img src={g8} alt="Goal rate vs distance" className="graph-image" />
          <p className="graph-text">
            Shows how scoring probability decreases as distance increases, using
            evenly sized distance bins.
          </p>
        </article>

        {/* 9. Shot Location Density */}
        <article className="graph-card">
          <h2 className="graph-title">Shot Location Density</h2>
          <img src={g9} alt="Shot location density" className="graph-image" />
          <p className="graph-text">
            Visualizes where shots most commonly occur on the ice and highlights
            high-volume shooting zones.
          </p>
        </article>

        {/* 10. Goal Rate Heatmap */}
        <article className="graph-card">
          <h2 className="graph-title">Goal Rate Heatmap</h2>
          <img src={g10} alt="Goal rate heatmap" className="graph-image" />
          <p className="graph-text">
            Gives a spatial view of where goals are most likely to occur,
            revealing the most dangerous areas on the ice.
          </p>
        </article>

        {/* 11. Shot Map by Shot Type */}
        <article className="graph-card">
          <h2 className="graph-title">Shot Map by Shot Type</h2>
          <img src={g11} alt="Shot map by shot type" className="graph-image" />
          <p className="graph-text">
            Highlights positional tendencies for each shot type, such as tips near
            the crease or slapshots near the point.
          </p>
        </article>

        {/* 12. Shooter Handedness and Goal Percentage */}
        <article className="graph-card">
          <h2 className="graph-title">Shooter Handedness &amp; Goal %</h2>
          <img src={g12} alt="Shooter handedness and goal percentage" className="graph-image" />
          <p className="graph-text">
            Compares goal scoring efficiency between left-handed and right-handed
            shooters.
          </p>
        </article>

        {/* 13. Goal Rate on Rush vs Not */}
        <article className="graph-card">
          <h2 className="graph-title">Goal Rate: Rush vs Non-Rush</h2>
          <img src={g13} alt="Goal rate on rush vs non-rush" className="graph-image" />
          <p className="graph-text">
            Measures how dangerous rush shots are relative to settled offensive
            zone play.
          </p>
        </article>

        {/* 14. Goal Rate by Rebound */}
        <article className="graph-card">
          <h2 className="graph-title">Goal Rate: Rebound vs Non-Rebound</h2>
          <img src={g14} alt="Goal rate by rebound" className="graph-image" />
          <p className="graph-text">
            Shows how rebounds significantly increase scoring probability due to
            goalie movement and closer shot distance.
          </p>
        </article>
      </section>

      {/* Research Question Mapping */}
      <section className="discovery-questions">
        <h2 className="questions-title">Key Research Questions</h2>
        <p className="questions-intro">
          Our exploratory findings raised broader questions about fatigue, game
          flow, and context. These questions guided our deeper statistical
          analysis:
        </p>

        <ul className="questions-list">
          <li className="question-item">
            <strong>Do longer defending shifts increase goals?</strong>
          </li>
          <li className="question-item">
            <strong>Do home teams score more?</strong>
          </li>
          <li className="question-item">
            <strong>Does short offensive ice time boost scoring?</strong>
          </li>
          <li className="question-item">
            <strong>Are players fresh?</strong>
          </li>
          <li className="question-item">
            <strong>Does team rest gap affect scoring?</strong>
          </li>
          <li className="question-item">
            <strong>Does goalie fatigue increase goals?</strong>
          </li>
          <li className="question-item">
            <strong>Does consecutive passive play influence goal rate?</strong>
          </li>
        </ul>

        <p className="questions-outro">
          These questions connect shot-level patterns to larger ideas like
          fatigue, flow, and game strategy. They form the basis of our later
          modeling and conclusions.
        </p>
      </section>
    </main>
  );
};

export default Discovery;
